import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useStudents } from "@/hooks/useStudents";
import { groupByLine, LinePattern, classifyLine } from "@/lib/apartmentLines";
import { encodeApartmentId, inferLinePattern, PATTERN_LABEL } from "@/lib/apartmentInfer";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { withOrg } from "@/lib/org";
import { db } from "@/lib/firebase";
import { useAuth } from "@/store/auth";
import clsx from "clsx";
import { fmtHo, fmtYearMonth } from "@/lib/format";
import { useDongClaims } from "@/hooks/useDongClaims";
import { deliveryBlockReason } from "@/lib/deliveryGuard";
import { Building2, ChevronLeft, Edit3, CheckCircle2 } from "lucide-react";

export default function ApartmentView() {
  const { user, role } = useAuth();
  const { claims, loading: claimsLoading } = useDongClaims(fmtYearMonth());
  const { buildingKey } = useParams();
  const [gu, dong, buildingName] = decodeURIComponent(buildingKey || "").split("_");
  const { students } = useStudents({ gu, dong });
  const aptId = encodeApartmentId(gu, dong, buildingName);
  const [override, setOverride] = useState<any>(null);
  const [editing, setEditing] = useState(false);

  const relevant = useMemo(
    () => students.filter((s) => s.buildingName === buildingName && !s.courierFlag),
    [students, buildingName],
  );

  const inferred = useMemo(() => inferLinePattern(relevant), [relevant]);

  useEffect(() => {
    if (!aptId) return;
    const unsub = onSnapshot(doc(db, "apartments", aptId), (snap) => {
      setOverride(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [aptId]);

  const pattern: LinePattern = override?.verified?.pattern || inferred.pattern;
  const pLabel = PATTERN_LABEL[pattern];
  const verified = !!override?.verified?.pattern;

  const grouped = useMemo(
    () => groupByLine(relevant.map((s) => ({ ...s, lineOverride: undefined })), pattern),
    [relevant, pattern],
  );

  const sortedBuildings = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));

  const correctPattern = async (newPattern: LinePattern, note = "") => {
    await setDoc(doc(db, "apartments", aptId), withOrg({
      name: buildingName,
      gu, dong,
      householdCount: relevant.length,
      inferred,
      verified: {
        pattern: newPattern,
        verifiedBy: user?.email || user?.uid,
        verifiedAt: serverTimestamp(),
        source: "driver_feedback",
        note,
      },
      updatedAt: serverTimestamp(),
    }), { merge: true });
    setEditing(false);
  };

  if (!buildingName) return <div className="p-6">건물 정보 없음</div>;

  return (
    <div className="space-y-4">
      <Link to="/driver/route" className="text-sm text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> 경로로
      </Link>

      <div className="card p-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-brand text-white grid place-items-center">
            <Building2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h1 className="page-title">{buildingName}</h1>
            <div className="text-sm text-slate-500 mt-0.5">{gu} {dong} · 총 {relevant.length}가구</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 flex items-center gap-1">
              {verified ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> 확정</>
                : `추론 ${Math.round(inferred.confidence * 100)}%`}
            </div>
            <div className="text-2xl mt-0.5">{pLabel.emoji}</div>
          </div>
        </div>

        <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm flex items-center gap-2">
          <div className="flex-1">
            <div className="font-semibold">{pLabel.short}</div>
            <div className="text-xs text-slate-500">{pLabel.description}</div>
          </div>
          <button className="btn-secondary btn-sm" onClick={() => setEditing((v) => !v)}>
            <Edit3 className="w-3.5 h-3.5" /> 정정
          </button>
        </div>

        {editing && (
          <div className="mt-3 rounded-xl ring-1 ring-brand-200 bg-brand-50 p-3 space-y-2 animate-fade-in">
            <div className="text-xs font-semibold text-brand-700">현장 피드백 — 이 단지의 실제 구조는?</div>
            <div className="grid grid-cols-1 gap-1.5">
              {(Object.entries(PATTERN_LABEL) as [LinePattern, typeof PATTERN_LABEL[LinePattern]][])
                .filter(([p]) => p !== "custom")
                .map(([p, lbl]) => (
                  <button
                    key={p}
                    className={clsx(
                      "rounded-lg border px-3 py-2 text-left text-sm transition bg-white",
                      pattern === p ? "border-brand-400 bg-brand-100/50" : "border-slate-200 hover:border-brand-300",
                    )}
                    onClick={() => correctPattern(p)}
                  >
                    <div className="font-semibold">{lbl.emoji} {lbl.short}</div>
                    <div className="text-xs text-slate-500">{lbl.description}</div>
                  </button>
                ))}
            </div>
            <div className="text-2xs text-slate-500">수정 즉시 모든 기사 앱에 반영됩니다.</div>
          </div>
        )}
      </div>

      <div className="card p-3 text-xs text-slate-600">
        💡 같은 라인(현관/엘리베이터 공용) 호수는 한번에 엘리베이터로 올라가 배송하세요. ⬇ 층 내림차순 — 옥상→1층 순 배송 추천.
      </div>

      <div className="space-y-4">
        {sortedBuildings.map((b) => {
          const lines = grouped[b];
          return (
            <div key={b} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold text-lg">
                  {b !== "-" ? `${b}동` : "동 미상"}
                </div>
                <div className="text-sm text-slate-500">
                  {lines.reduce((s, l) => s + l.students.length, 0)}가구
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lines.map((lg) => (
                  <div
                    key={lg.line.groupIndex}
                    className={clsx(
                      "rounded-2xl p-3 border-2",
                      lg.line.groupIndex === 0 ? "border-brand-200 bg-brand-50/50" :
                      lg.line.groupIndex === 1 ? "border-amber-200 bg-amber-50/50" :
                      lg.line.groupIndex === 2 ? "border-emerald-200 bg-emerald-50/50" :
                      "border-slate-200 bg-slate-50",
                    )}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-bold">🛗 {lg.line.groupLabel}</div>
                        <div className="text-xs text-slate-500">{lg.line.elevatorHint}</div>
                      </div>
                      <span className="chip-neutral">{lg.students.length}가구</span>
                    </div>
                    <div className="space-y-1">
                      {lg.students.map((s) => {
                        const info = classifyLine(s, pattern);
                        // 쉬는 집·남의 담당 집은 보여만 주고 배송 화면으로 들어가지 않게 한다(4차 점검 18)
                        const why = claimsLoading ? "" : deliveryBlockReason(s, claims, user?.uid, role === "admin");
                        if (why) {
                          return (
                            <div key={s.id} className="rounded-xl px-3 py-2 bg-slate-100 border border-slate-200 text-slate-500">
                              <div className="flex items-center justify-between">
                                <div className="font-medium text-sm">{fmtHo(s.buildingHo)} · {s.name}</div>
                                <div className="chip-neutral">{info.unitNumber}</div>
                              </div>
                              <div className="text-xs text-rose-700 mt-0.5 break-keep">🚫 {why}</div>
                            </div>
                          );
                        }
                        return (
                          <Link
                            key={s.id}
                            to={`/driver/route/${s.id}`}
                            className="flex items-center justify-between rounded-xl px-3 py-2 bg-white hover:bg-slate-50 border border-slate-100 transition"
                          >
                            <div>
                              <div className="font-medium text-sm">{fmtHo(s.buildingHo)} · {s.name}</div>
                              <div className="text-xs text-slate-500">{s.school} · {s.guardian}</div>
                            </div>
                            <div className="chip-neutral">{info.unitNumber}</div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
